import { useState, type ChangeEvent, type ReactNode } from "react";
import { resources } from "@/lib/resources";
import { useFetch } from "@/hooks/useFetch";
import { apiErrorMessage } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { FormInput } from "@/components/ui/FormInput";
import { FullScreenSpinner } from "@/components/ui/FullScreenSpinner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MY_BANKS } from "@shared/banks";

/**
 * The affiliate dashboard's one-time door: a WhatsApp number and a bank account
 * before anything else. Promoting never waited for these — the share card asks
 * for neither, so nobody is put off before they have even shared — but money
 * cannot be paid without them, so this is where they are asked for, and there
 * is no way past it until they are given.
 */
export function AffiliateCompletionGate({ children }: { children: ReactNode }) {
  const { data, loading, reload } = useFetch(() => resources.affiliatePortal.completion(), []);
  if (loading && !data) return <FullScreenSpinner />;
  // A failed probe is not a reason to lock someone out of their own dashboard.
  if (!data) return <>{children}</>;
  // Booleans: whether each is on file, never the number or the account itself.
  const { whatsapp: hasNumber, bank: hasBank, owedCents } = data;
  if (hasNumber && hasBank) return <>{children}</>;
  return <CompleteAccount owedCents={owedCents} hasWhatsapp={hasNumber} hasBank={hasBank} onDone={reload} />;
}

function CompleteAccount({
  owedCents,
  hasWhatsapp,
  hasBank,
  onDone,
}: {
  owedCents: number;
  hasWhatsapp: boolean;
  hasBank: boolean;
  onDone: () => void;
}) {
  const [whatsapp, setWhatsapp] = useState("");
  const [bankName, setBankName] = useState("");
  const [holder, setHolder] = useState("");
  const [number, setNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ready =
    (hasWhatsapp || whatsapp.replace(/\D/g, "").length >= 9) &&
    (hasBank || (bankName.trim().length > 1 && holder.trim().length > 1 && number.replace(/\D/g, "").length >= 6));

  async function save() {
    setErr(null);
    setSaving(true);
    try {
      if (!hasWhatsapp) await resources.affiliatePortal.saveContact({ whatsapp: whatsapp.trim() });
      if (!hasBank) await resources.affiliatePortal.saveBank({ bankName, accountHolder: holder, accountNumber: number });
      onDone();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-50 px-4 py-10 sm:items-center">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="aff-complete">
        <h1 className="text-lg font-semibold text-slate-900">Lengkapkan akaun affiliate anda</h1>
        <p className="mt-1 text-sm text-slate-500">Supaya komisen boleh dibayar terus ke akaun anda.</p>
        {owedCents > 0 && (
          <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="aff-complete-owed">
            Komisen terkumpul: <b>{formatCurrency(owedCents / 100, "MYR")}</b>
          </p>
        )}

        <div className="mt-5 space-y-4">
          {!hasWhatsapp && (
            <FormInput
              id="aff-complete-whatsapp"
              label="No. WhatsApp"
              value={whatsapp}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setWhatsapp(e.target.value)}
              inputMode="tel"
              autoComplete="tel"
              placeholder="012-345 6789"
              hint="Penjual guna nombor ini untuk hubungi anda tentang bayaran."
            />
          )}
          {!hasBank && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="aff-complete-bank">
                  Bank
                </label>
                <SearchableSelect
                  items={[...MY_BANKS]}
                  value={bankName}
                  onChange={setBankName}
                  getKey={(b) => b}
                  getLabel={(b) => b}
                  searchPlaceholder="Cari bank…"
                  id="aff-complete-bank"
                />
              </div>
              <FormInput
                id="aff-complete-holder"
                label="Nama pemegang akaun"
                value={holder}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setHolder(e.target.value)}
                autoComplete="name"
              />
              <FormInput
                id="aff-complete-number"
                label="No. akaun"
                value={number}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setNumber(e.target.value)}
                inputMode="numeric"
                hint="Disimpan secara tersulit. Hanya 4 digit akhir dipaparkan selepas ini."
              />
            </>
          )}
          {err && (
            <p className="text-sm text-rose-600" data-testid="aff-complete-error">
              {err}
            </p>
          )}
          <Button className="w-full" onClick={() => void save()} disabled={!ready || saving} data-testid="aff-complete-save">
            {saving ? "Menyimpan…" : "Simpan & buka dashboard"}
          </Button>
        </div>
      </div>
    </div>
  );
}
